import React, { useCallback, memo, useState, useEffect } from 'react';
import {
  Checkbox,
  InputWithMask,
} from '@leasequery/lq.react-component-library';
import { SelectOption } from '@leasequery/lq.react-component-library/dist/models';
import {
  ReportCurrency,
  ReportType,
  ClientCustomCalendarModel,
  CustomCalendarPeriods,
} from '../../../../models';
import * as Styled from '../styled';
import { INPUT_MASK } from '../../../../constants';
import { validate } from '../../../../util/validation';
import { ReportTypeSetting } from '../../../../models/reportTypeSetting';
import { format } from 'date-fns';
import { DATE_TIME_FORMAT } from '../../../../constants';
import ReportSettingPIT from '../ReportSettingPIT';
import { Icon } from '../../../../components';
import { useAuth } from '../../../../context';

interface ReportSettingWeightedAverageProps {
  onChange: (updatedValue: ReportSettingWeightedAverageState) => void;
  reportSettingState: ReportSettingWeightedAverageState;
  reportTypeSetting: ReportTypeSetting;
  isAllowPIT: boolean;
  customCalendarSettings?: ClientCustomCalendarModel;
}

export interface ReportSettingWeightedAverageState {
  selectedCurrency: SelectOption<ReportCurrency>;
  asOf: string;
  isRunByTransitionDate: boolean;
  isValid: boolean;
}

function ReportDateSettingWeightedAverage({
  onChange,
  reportSettingState,
  reportTypeSetting,
  isAllowPIT,
  customCalendarSettings,
}: ReportSettingWeightedAverageProps): JSX.Element {
  const [toggelAsOf, setToggleAsOf] = useState(
    reportSettingState.isRunByTransitionDate,
  );
  const { isCustomCalendarEnabled, numberOfPeriods } = customCalendarSettings;
  const { client } = useAuth();
  const placeholder = isCustomCalendarEnabled
    ? INPUT_MASK.CUSTOM_CALENDAR_PLACEHOLDER
    : INPUT_MASK.SHORT_DATE_PLACEHOLDER;

  const mask = isCustomCalendarEnabled
    ? INPUT_MASK.CUSTOM_CALENDAR
    : INPUT_MASK.SHORT_DATE;

  const dateInputHandler = useCallback(
    (value: string): void => {
      let isValidDate;

      if (isCustomCalendarEnabled) {
        isValidDate =
          validate(value, CustomCalendarPeriods[numberOfPeriods]) === '';
      } else {
        isValidDate = validate(value, { shortDate: 'Invalid date' }) === '';
      }

      onChange({
        ...reportSettingState,
        asOf: value,
        isValid: isValidDate,
      });
    },
    [onChange, isCustomCalendarEnabled, numberOfPeriods, reportSettingState],
  );

  const getTransitionDate = useCallback((): string => {
    const transitionMonth =
      client.jurisdiction === 'IFRS'
        ? reportTypeSetting.ifrsTransitionMonth
        : reportTypeSetting.effectiveMonth;

    const transitionYear =
      client.jurisdiction === 'IFRS'
        ? reportTypeSetting.ifrsTransitionYear
        : reportTypeSetting.effectiveYear;

    return format(
      new Date(transitionYear, transitionMonth - 1),
      DATE_TIME_FORMAT.MONTH_YEAR,
    );
  }, [client.jurisdiction, reportTypeSetting]);

  const runByTransitionDateHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const isChecked = e.currentTarget.checked;
      setToggleAsOf(isChecked);

      onChange({
        ...reportSettingState,
        asOf: getTransitionDate(),
        isRunByTransitionDate: isChecked,
        isValid: true,
      });
    },
    [onChange, reportSettingState, getTransitionDate],
  );

  useEffect(() => {
    if (reportSettingState.isRunByTransitionDate) {
      setToggleAsOf(true);
      onChange({
        ...reportSettingState,
        asOf: getTransitionDate(),
        isRunByTransitionDate: true,
        isValid: true,
      });
    }
  // Intentionally omit `onChange`, `getTransitionDate`, and full `reportSettingState` —
  // including them causes an infinite re-render loop because all three are recreated on
  // every parent render. This effect should only fire when the transition-date toggle changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSettingState.isRunByTransitionDate]);

  return (
    <>
      <Styled.ReportSettingRow>
        <div>
          <Icon name="calendar" />
        </div>
        <div className="shortDate">
          <InputWithMask
            placeholder={placeholder}
            required={true}
            name="asOf"
            value={reportSettingState.asOf}
            onChange={useCallback(
              (event): void => {
                dateInputHandler(event.target.value.trim());
              },
              [dateInputHandler],
            )}
            mask={mask}
            label="Report Start"
            disabled={toggelAsOf}
          />
          <div className="item">
            <Checkbox
              name="isRunByTransitionDate"
              label="Run By Transition Date"
              onChange={runByTransitionDateHandler}
              checked={reportSettingState.isRunByTransitionDate}
            />
          </div>
        </div>
      </Styled.ReportSettingRow>
      <ReportSettingPIT
        reportType={ReportType.weightedAverage}
        isAllowPIT={isAllowPIT}
        reportSettingState={reportSettingState}
        reportTypeSetting={reportTypeSetting}
        customCalendarSettings={customCalendarSettings}
        onChange={useCallback(
          (state) => onChange(state as ReportSettingWeightedAverageState),
          [onChange],
        )}
      />
    </>
  );
}

export default memo(ReportDateSettingWeightedAverage);
